import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faToggleOff,
  faToggleOn,
  faTrash,
  faPlus,
  faFileImport,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../components/Admin/AdminActionButton';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import Dialogs from '../../../components/Common/Dialog';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import PaginationButtons from '../../../components/Common/Pagination';
import ImportDialog from '../../../components/Common/ImportDialog';
import { Toast } from '../../../components/Common/Toast';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import SchoolForm from './SchoolForm';
import TeachersDialog from './TeachersDialog';
import {
  SchoolModel,
  getAdminSchools,
  saveSchool,
  updateSchool,
  deleteSchool,
  changeSchoolStatus,
  importPublicSchoolsCsv,
  importPrivateSchoolsCsv,
  deleteAllSchools,
  getTeacherCountsBySchoolIds,
} from './services/School.service';
import type { ImportProgressData } from '../../../utils/sseUpload';

const IS_LOCAL = process.env.REACT_APP_ENV === 'localhost' || process.env.REACT_APP_ENV === 'local';

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'nces_public', label: 'NCES Public' },
  { value: 'nces_private', label: 'NCES Private' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
  { value: 'clever', label: 'Clever' },
];

const formatDate = (d: any): string => {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d._seconds ? new Date(d._seconds * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Schools = ({ onOptionClick }: { onOptionClick?: import('../adminNav').AdminNavHandler }) => {
  const [schools, setSchools] = useState<SchoolModel[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState<Partial<SchoolModel> | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'status' | 'delete'>('status');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [importDialogType, setImportDialogType] = useState<'public' | 'private' | null>(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [teacherCounts, setTeacherCounts] = useState<Record<string, number>>({});
  const [teachersDialogSchool, setTeachersDialogSchool] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const loadSchools = useCallback(
    (page?: number, state?: string, search?: string, source?: string, country?: string) => {
      setLoading(true);
      getAdminSchools(
        (data) => {
          setSchools(data);
          setLoading(false);
          // Fetch teacher counts for the loaded page of schools
          const ids = data.map((s) => s.id).filter(Boolean);
          if (ids.length > 0) {
            getTeacherCountsBySchoolIds(ids).then((counts) => setTeacherCounts(counts));
          }
        },
        (pages) => setTotalPages(pages),
        page || currentPage,
        state ?? (stateFilter || undefined),
        search ?? (searchText || undefined),
        source ?? (sourceFilter || undefined),
        (tr) => setTotalRecords(tr),
        country ?? (countryFilter || undefined),
      );
    },
    [currentPage, stateFilter, searchText, sourceFilter, countryFilter]
  );

  useEffect(() => {
    loadSchools(currentPage, stateFilter || undefined, searchText || undefined, sourceFilter || undefined, countryFilter || undefined);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-apply when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadSchools(1, stateFilter || undefined, searchText || undefined, sourceFilter || undefined, countryFilter || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [stateFilter, sourceFilter, countryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    loadSchools(1, stateFilter || undefined, value || undefined, sourceFilter || undefined, countryFilter || undefined);
  };

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages) setCurrentPage(+page);
  };

  const handleCreate = () => {
    setEditingSchool(null);
    setShowForm(true);
  };

  const handleEdit = (school: SchoolModel) => {
    setEditingSchool({ ...school });
    setShowForm(true);
  };

  const reload = () =>
    loadSchools(currentPage, stateFilter || undefined, searchText || undefined, sourceFilter || undefined, countryFilter || undefined);

  const handleSave = async (data: Partial<SchoolModel>) => {
    if (data.id) {
      await updateSchool(data as Partial<SchoolModel> & { id: string }, () => {
        setShowForm(false);
        reload();
      });
    } else {
      await saveSchool(data, () => {
        setShowForm(false);
        reload();
      });
    }
  };

  const handleToggleStatus = (id: string) => {
    setSelectedId(id);
    setDialogAction('status');
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setDialogAction('delete');
    setOpenDialog(true);
  };

  const handleDialogConfirm = async () => {
    if (!selectedId) return;
    setOpenDialog(false);

    if (dialogAction === 'status') {
      await changeSchoolStatus(selectedId, reload);
    } else {
      await deleteSchool(selectedId, reload);
    }
    setSelectedId(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  const handleImportFile = async (file: File, onProgress?: (data: ImportProgressData) => void) => {
    if (importDialogType === 'public') return importPublicSchoolsCsv(file, onProgress);
    return importPrivateSchoolsCsv(file, onProgress);
  };

  const handleClearAll = async () => {
    setClearAllDialogOpen(false);
    setClearingAll(true);
    const result = await deleteAllSchools();
    setClearingAll(false);
    if (result.success) {
      Toast(result.message);
      reload();
    } else {
      Toast(result.message, true);
    }
  };

  const rowOffset = (currentPage - 1) * 25;

  return (
    <div className="p-0 md:p-4">
      {showForm ? (
        <SchoolForm
          setShowForm={setShowForm}
          schoolDetails={editingSchool}
          onSave={handleSave}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex justify-between items-center flex-wrap gap-2 my-5">
            <div className="flex items-center gap-2">
              <AdminSearchInput
                placeholder="Search name, city, district..."
                onSearch={handleSearchChange}
                className="w-64"
              />
              <input
                type="text"
                placeholder="State"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
                className="border border-gray-300 rounded-md px-2 py-1.5 w-20 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                maxLength={2}
              />
              <input
                type="text"
                placeholder="Country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value.toUpperCase())}
                className="border border-gray-300 rounded-md px-2 py-1.5 w-24 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                maxLength={2}
              />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              {IS_LOCAL && (
                <button
                  onClick={() => setClearAllDialogOpen(true)}
                  disabled={clearingAll}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md hover:shadow-md transition-shadow text-sm disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {clearingAll ? 'Clearing...' : 'Clear All'}
                </button>
              )}
              <button
                onClick={() => setImportDialogType('public')}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:shadow-md transition-shadow text-sm"
              >
                <FontAwesomeIcon icon={faFileImport} />
                Import Public
              </button>
              <button
                onClick={() => setImportDialogType('private')}
                className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-md hover:shadow-md transition-shadow text-sm"
              >
                <FontAwesomeIcon icon={faFileImport} />
                Import Private
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-storyblue text-white rounded-md hover:shadow-md transition-shadow"
              >
                <FontAwesomeIcon icon={faPlus} />
                New School
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="w-full overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <AdminTableHeader
                    columns={[
                      { key: 'sn', label: 'S#' },
                      { key: 'name', label: 'Name' },
                      { key: 'ncesId', label: 'NCES ID' },
                      { key: 'state', label: 'State' },
                      { key: 'city', label: 'City' },
                      { key: 'district', label: 'District' },
                      { key: 'teachers', label: 'Teachers' },
                      { key: 'source', label: 'Source' },
                      { key: 'updated', label: 'Updated' },
                      { key: 'status', label: 'Status' },
                      { key: 'actions', label: 'Actions' },
                    ]}
                  />
                  <tbody>
                    {schools && schools.length > 0 ? (
                      schools.map((school, index) => (
                        <tr key={school.id} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                          <td className="text-left py-2 px-4 border-b">{rowOffset + index + 1}</td>
                          <td className="text-left py-2 px-4 border-b">{school.name}</td>
                          <td className="text-left py-2 px-4 border-b text-xs font-mono">{school.externalId || '-'}</td>
                          <td className="text-left py-2 px-4 border-b">{school.state}</td>
                          <td className="text-left py-2 px-4 border-b">{school.city || '-'}</td>
                          <td className="text-left py-2 px-4 border-b">
                            {school.districtName || '-'}
                          </td>
                          <td className="text-left py-2 px-4 border-b text-center">
                            <button
                              onClick={() => setTeachersDialogSchool({ id: school.id, name: school.name })}
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                (teacherCounts[school.id] || 0) > 0
                                  ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                              title="View linked teachers"
                            >
                              {teacherCounts[school.id] ?? '-'}
                            </button>
                          </td>
                          <td className="text-left py-2 px-4 border-b">
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              {school.source}
                            </span>
                          </td>
                          <td className="text-left py-2 px-4 border-b text-xs text-gray-500">
                            {formatDate(school.updatedAt)}
                          </td>
                          <td className="text-left py-2 px-4 border-b">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                school.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {school.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-left py-4 px-4 border-b">
                            <div className="flex gap-3">
                              <AdminActionButton
                                icon={faPenToSquare}
                                title="Edit"
                                hoverColor="hover:text-blue-600"
                                onClick={() => handleEdit(school)}
                              />
                              <AdminActionButton
                                icon={school.isActive ? faToggleOn : faToggleOff}
                                title={school.isActive ? 'Deactivate' : 'Activate'}
                                className={school.isActive ? 'text-storygreen' : 'text-red-500'}
                                hoverColor={school.isActive ? 'hover:text-green-700' : 'hover:text-red-700'}
                                onClick={() => handleToggleStatus(school.id)}
                              />
                              <AdminActionButton
                                icon={faTrash}
                                title="Delete"
                                hoverColor="hover:text-red-600"
                                onClick={() => handleDelete(school.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <NoRecordFound colSpan={11} />
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center mt-4">
                <TableInfoBar
                  currentPage={currentPage}
                  pageSize={25}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  recordsOnPage={schools.length}
                />
                {totalPages > 1 && (
                  <PaginationButtons
                    totalPages={totalPages}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                  />
                )}
              </div>
            </>
          )}

          <Dialogs
            openDialog={openDialog}
            handelClose={handleDialogClose}
            confirmMessage={
              dialogAction === 'status'
                ? 'Are you sure you want to change the school status?'
                : 'Are you sure you want to delete this school?'
            }
            handelProcess={handleDialogConfirm}
          />

          <ImportDialog
            open={importDialogType !== null}
            title={importDialogType === 'public' ? 'Import Public Schools' : 'Import Private Schools'}
            onClose={() => setImportDialogType(null)}
            onImport={handleImportFile}
            onComplete={reload}
          />

          {IS_LOCAL && (
            <Dialogs
              openDialog={clearAllDialogOpen}
              handelClose={() => setClearAllDialogOpen(false)}
              confirmMessage="Are you sure you want to DELETE ALL schools? This cannot be undone."
              handelProcess={handleClearAll}
            />
          )}

          <TeachersDialog
            open={teachersDialogSchool !== null}
            schoolId={teachersDialogSchool?.id || ''}
            schoolName={teachersDialogSchool?.name || ''}
            onClose={() => setTeachersDialogSchool(null)}
            onNavigateToCustomer={
              onOptionClick
                ? (email) => onOptionClick('Customers', { teacherEmail: email })
                : undefined
            }
          />
        </>
      )}
    </div>
  );
};

export default Schools;
